import React, { useEffect, useState } from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { translations } from '../i18n'
import { getPosts, subscribeBlog, resetSubscribe } from '../actions/blogActions'
import { toggleReadLater } from '../actions/blogActions'
import Loader from '../components/Loader'
import Message from '../components/Message'

function SubscribeForm({ T, lang }) {
  const dispatch = useDispatch()
  const { loading, success, error } = useSelector(s => s.rootReducer.subscribeRed)
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [notifLang, setNotifLang] = useState(lang === 'it' ? 'it' : 'en')
  const [consent, setConsent] = useState(false)
  const [localError, setLocalError] = useState('')

  useEffect(() => () => { dispatch(resetSubscribe()) }, [dispatch])

  const submit = (e) => {
    e.preventDefault()
    setLocalError('')
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setLocalError(T.blog.subscribe_email_required); return }
    if (!consent) { setLocalError(T.blog.subscribe_consent_required); return }
    dispatch(subscribeBlog({
      email: email.trim(),
      language: notifLang,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      consent: true,
    }))
  }

  // 16px inputs avoid iOS zoom-on-focus; white fill so they read as active on navy.
  const inputStyle = {
    width: '100%', padding: '.72rem .8rem', borderRadius: '8px',
    border: '1px solid rgba(255,255,255,.25)', fontSize: '16px',
    background: '#fff', color: 'var(--navy)',
  }

  return (
    <div id='subscribe' style={{
      scrollMarginTop: '90px',
      background: 'radial-gradient(120% 140% at 100% 0%, rgba(0,172,193,.18) 0%, transparent 55%), var(--navy)',
      color: '#fff', borderRadius: '14px', borderTop: '4px solid var(--teal)',
      padding: '2.25rem 2rem', marginTop: '3rem',
      boxShadow: '0 12px 30px rgba(27,42,74,.22)',
    }}>
      <h2 style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1.6rem', color: '#fff', marginBottom: '.35rem' }}>
        <span style={{ marginRight: '.5rem' }} aria-hidden='true'>✉️</span>{T.blog.subscribe_title}
      </h2>
      <p style={{ color: '#cbd5e6', fontSize: '.98rem', marginBottom: '1.4rem', maxWidth: '46ch' }}>{T.blog.subscribe_intro}</p>

      {success ? (
        <Message variant='success'>{T.blog.subscribe_success}</Message>
      ) : (
        <form onSubmit={submit}>
          {/* Focal row: email + CTA */}
          <Row className='g-2 align-items-stretch'>
            <Col xs={12} md={8}>
              <input type='email' style={inputStyle} placeholder={T.blog.subscribe_email}
                     value={email} onChange={e => setEmail(e.target.value)} required aria-label={T.blog.subscribe_email} />
            </Col>
            <Col xs={12} md={4}>
              <button type='submit' disabled={loading} style={{
                width: '100%', height: '100%', minHeight: '48px',
                background: 'var(--teal-mid)', color: '#fff', border: 'none', borderRadius: '8px',
                padding: '.6rem 1.4rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1rem',
                letterSpacing: '.2px', boxShadow: '0 6px 16px rgba(0,172,193,.4)',
                cursor: loading ? 'default' : 'pointer', opacity: loading ? .65 : 1,
              }}>
                {loading ? T.blog.subscribe_sending : `${T.blog.subscribe_submit} →`}
              </button>
            </Col>
          </Row>

          {/* Secondary row: optional names + language */}
          <Row className='g-2 mt-1'>
            <Col xs={12} md={4}>
              <input type='text' style={inputStyle} placeholder={T.blog.subscribe_first}
                     value={firstName} onChange={e => setFirstName(e.target.value)} aria-label={T.blog.subscribe_first} />
            </Col>
            <Col xs={12} md={4}>
              <input type='text' style={inputStyle} placeholder={T.blog.subscribe_last}
                     value={lastName} onChange={e => setLastName(e.target.value)} aria-label={T.blog.subscribe_last} />
            </Col>
            <Col xs={12} md={4}>
              <select style={inputStyle} value={notifLang} onChange={e => setNotifLang(e.target.value)} aria-label={T.blog.subscribe_lang}>
                <option value='en'>English</option>
                <option value='it'>Italiano</option>
              </select>
            </Col>
          </Row>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '.5rem', marginTop: '1rem', fontSize: '.9rem', color: '#b9c4d6' }}>
            <input type='checkbox' checked={consent} onChange={e => setConsent(e.target.checked)}
                   style={{ marginTop: '.2rem', accentColor: 'var(--teal)' }} />
            <span>{T.blog.subscribe_consent}</span>
          </label>

          {(localError || error) && <div style={{ marginTop: '1rem' }}><Message variant='danger'>{localError || T.blog.subscribe_error}</Message></div>}

          <p style={{ marginTop: '1rem', fontSize: '.78rem', color: '#93a1b5', lineHeight: 1.5 }}>{T.blog.subscribe_note}</p>
        </form>
      )}
    </div>
  )
}

function BlogScreen() {
  const dispatch = useDispatch()
  const { lang } = useSelector(s => s.rootReducer.langRed)
  const T = translations[lang]
  const { loading, error, posts = [] } = useSelector(s => s.rootReducer.postsRed)
  const { ids: savedIds = [] } = useSelector(s => s.rootReducer.readLaterRed)

  useEffect(() => { dispatch(getPosts()) }, [dispatch])

  const title   = (p) => p[`title_${lang}`]   || p.title_en
  const excerpt = (p) => p[`excerpt_${lang}`] || p.excerpt_en

  const scrollToSubscribe = () => {
    document.getElementById('subscribe')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (loading) return <div className='fc-section'><Container><Loader /></Container></div>
  if (error)   return <div className='fc-section'><Container><Message variant='danger'>{error}</Message></Container></div>

  return (
    <div>
      <div style={{ background: 'var(--navy)', color: '#fff', padding: '48px 0 36px' }}>
        <Container>
          <h1 style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, marginBottom: '.4rem' }}>{T.sections.blog}</h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '.5rem 1rem' }}>
            <span style={{ color: 'var(--teal)', fontWeight: 500 }}>{posts.length} posts</span>
            <button type='button' onClick={scrollToSubscribe} style={{
              background: 'transparent', color: 'var(--teal)', border: '1.5px solid var(--teal)',
              borderRadius: '999px', padding: '.4rem .9rem', fontWeight: 600, fontSize: '.9rem', cursor: 'pointer',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,151,167,.15)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}>
              {T.blog.subscribe_hero_cta} →
            </button>
          </div>
        </Container>
      </div>
      <div className='fc-section'>
        <Container>
          <Row className='g-4'>
            {posts.map(post => {
              const saved = savedIds.includes(post.id)
              return (
                <Col md={6} lg={4} key={post.id}>
                  <div className='blog-card'>
                    {post.image
                      ? <img src={post.image} alt={title(post)} className='blog-card-img' />
                      : <div className='blog-card-img-placeholder'>✍️</div>
                    }
                    <div className='blog-card-body'>
                      <div className='blog-card-date'>
                        {new Date(post.date).toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </div>
                      <div className='blog-card-title'>{title(post)}</div>
                      <div className='blog-card-excerpt'>{excerpt(post)}</div>
                    </div>
                    <div className='blog-card-footer'>
                      <div>{(post.tags || []).map(tag => <span key={tag.id} className='tag-pill'>{tag.caption}</span>)}</div>
                      <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center' }}>
                        <button className={`btn-read-later ${saved ? 'saved' : ''}`} onClick={() => dispatch(toggleReadLater(post.id))}>
                          {saved ? T.blog.remove : T.blog.save}
                        </button>
                        <Link to={`/blog/${post.slug}/`} style={{ color: 'var(--teal)', fontSize: '.85rem', fontWeight: 500 }}>{T.blog.read_more} →</Link>
                      </div>
                    </div>
                  </div>
                </Col>
              )
            })}
          </Row>
          <SubscribeForm T={T} lang={lang} />
        </Container>
      </div>
    </div>
  )
}

export default BlogScreen
